<template>
  <v-layout column>
    <v-repeat-load-group-dialog v-if="transaction"
                                v-model="showCopyLoadDialog"
                                :loadGroupId="loadGroupId"
                                :sponsor="loadGroupSponsorName(loadGroup)"
                                :origin="loadGroup.details | originName"
                                :destination="loadGroup.details | destinationName"
                                :count="loadGroup.loadCount || 1"
                                :isMobile="isMobile"
                                :loading="loadingBtn"
                                :company="company"
                                :load-group="loadGroup"
                                @cancel="closeCopyLoadDialog"
                                @newLoad="closeCopyLoadDialog(true)"
    >
    </v-repeat-load-group-dialog>

    <v-toolbar height="56" v-if="!transaction">
      <v-toolbar-title>
        {{ bannerTitle }}
      </v-toolbar-title>
    </v-toolbar>
    <v-toolbar height="58" v-if="transaction">
      <v-toolbar-title>
        {{ transaction.loadHumanId }}, ${{ transaction.amount }}
      </v-toolbar-title>

      <v-layout :slot="isMobile ? 'extension' : null">
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn small flat color="primary"
                 :loading="updatingTransaction"
                 @click="refreshTransaction(transaction)">
            <v-layout column>
              <v-icon>refresh</v-icon>
              Reload
            </v-layout>
          </v-btn>
          <v-tabs v-model="tab"
                  :right="!isMobile"
                  :centered="isMobile"
                  grow show-arrows
                  color="#f5f5f5"
                  slider-color="primary">
            <v-tab key="Details">
              <v-layout column class="align-center">
                <v-icon>local_atm</v-icon>
                Details
              </v-layout>
            </v-tab>
            <v-tab key="Load">
              <v-layout column class="align-center">
                <v-icon>list</v-icon>
                Load
              </v-layout>
            </v-tab>
            <v-tab key="History">
              <v-layout column class="align-center">
                <v-icon>timeline</v-icon>
                <v-progress-linear class="mt-2 mb-0"
                                   v-if="loadingTransactionHistory"
                                   :indeterminate="true">
                </v-progress-linear>
                <template v-else>
                  History
                </template>
              </v-layout>
            </v-tab>
            <v-tab key="Map">
              <v-layout column class="align-center">
                <v-icon>map</v-icon>
                Map
              </v-layout>
            </v-tab>
          </v-tabs>
        </v-toolbar-items>
      </v-layout>
    </v-toolbar>

    <div class="pt-1" :class="{'scroll-section': !isMobile}">
      <v-card flat>
        <v-tabs-items v-model="tab" v-if="tab != null">
          <v-tab-item key="Details" :transition="false" :reverse-transition="false">
            <v-load-transaction-status-details class="mt-2"
                                               @reset-selected="$emit('reset-selected')"
                                               :company="company"
                                               :invoice="invoice"
                                               :bill="bill"
                                               :transaction="transaction">

            </v-load-transaction-status-details>
          </v-tab-item>
          <v-tab-item key="Load" :transition="false" :reverse-transition="false">
            <v-edit-load-details v-if="transaction"
                                 :company="company"
                                 transaction
                                 @refresh="$emit('updateLoad',$event)"
                                 @savingLoad="$emit('savingLoad', $event)"
                                 :planning-load="transaction.load.tacticalInfo">
              <v-toolbar-items slot="toolbarItems">
                <slot name="toolbarButtons">
                  <v-btn flat color="accent" @click="openCopyLoadDialog">
                    <v-layout column>
                      <v-icon>redo</v-icon>
                      Repeat
                    </v-layout>
                  </v-btn>
                </slot>
              </v-toolbar-items>
            </v-edit-load-details>
          </v-tab-item>
          <v-tab-item key="History" :transition="false"
                      :reverse-transition="false">
            <v-load-transaction-histories :company="company"
                                          :transaction="transaction">
            </v-load-transaction-histories>
          </v-tab-item>
          <v-tab-item key="Map" :transition="false" :reverse-transition="false">
            <slot name="loadMap">
              <load-map class="py-1 px-1"
                        hideOnSmall
                        v-if="transaction"
                        :load="transaction.load"
                        :origin-place-id="_.get(transaction, 'load.tacticalInfo.loadGroup.details.pickupLocation.googlePlace.placeId', null)"
                        :destination-place-id="_.get(transaction, 'load.tacticalInfo.loadGroup.details.dropoffLocation.googlePlace.placeId', null)">
              </load-map>
            </slot>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import companyNames from '@/mixins/companyNames';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import loadTransactionsMixin from '@/mixins/loadTransactionsMixin';

import LoadMap from '@/components/LoadMap';
import VEditLoadDetails from '@/components/InfoWidgets/EditLoadDetails';
import VBooked from '@/pages/Companies/Loads/components/Booked';
import VLoadTransactionHistories from '@/components/InfoWidgets/LoadTransactionHistories';
import VRepeatLoadGroupDialog from '@/components/RepeatLoadGroupDialog';
import VLoadTransactionStatusDetails from '@/components/InfoWidgets/LoadTransactionStatusDetails';

export default {
  name: 'VLoadTransactionDetails',
  mixins: [
    companyNames,
    loadGroupMixin,
    loadTransactionsMixin,
  ],
  components: {
    VRepeatLoadGroupDialog,
    VLoadTransactionStatusDetails,
    VLoadTransactionHistories,
    VBooked,
    LoadMap,
    VEditLoadDetails,
  },
  props: {
    transaction: {
      type: Object,
    },
    selectedLoad: {
      type: Object,
      default: null,
    },
    company: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    bannerTitle: {
      type: String,
      required: true,
    },
    invoice: {
      type: Boolean,
      default: false,
    },
    bill: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    _() {
      return _;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    loadingTransactionHistory() {
      // TODO: are we loading the transaction history?
      return false;
    },
    loadGroup() {
      return _.get(this.transaction, 'load.tacticalInfo.loadGroup');
    },
    loadGroupId() {
      return _.get(this.loadGroup, 'humanId');
    },
    isVendor() {
      return this.company.id === this.truckingCompany.id;
    },
    isCustomer() {
      return this.company.id === this.customer.id || this.company.id === this.owner.id;
    },

    owner() {
      return this.transactionLoadOwner(this.transaction);
    },
    truckingCompany() {
      return this.transactionTruckingCompany(this.transaction);
    },

    txnCustomerName() {
      return this.transaction.customer.companyName;
    },
    vendorName() {
      return this.transaction.vendor.companyName;
    },
  },
  methods: {
    countBookedLoads(loads) {
      return _.filter(loads, l => !this.isBookedLoadCanceled(l)).length;
    },
    isBookedLoadCanceled(booked) {
      return booked.loadStatus === 'CANCELED';
    },
    openCopyLoadDialog() {
      this.showCopyLoadDialog = true;
    },
    closeCopyLoadDialog(success) {
      this.showCopyLoadDialog = false;

      if (success) {
        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Load Group Created in the Planner',
          timeout: 3000,
        });
      }
    },
  },
  data: () => ({
    tab: null,

    showCopyLoadDialog: false,
    loadingBtn: false,
  }),
  created() {
    if (this.bill || this.invoice) {
      // well done. you did this right
    } else {
      throw new TypeError('Must specify either bill or invoice property as true');
    }
  },
};
</script>

<style scoped>
  .scroll-section {
    height: calc(100vh - 225px);
    overflow: auto;
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/simple/LoadTransactionDetails.vue